import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, ShieldAlert, FileSignature, User as UserIcon, CalendarPlus,
  BellPlus, CheckCircle2, Phone, Users, HandCoins, Undo2, StickyNote, Send, History,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RENEWAL_STATUS, RENEWAL_PRIORITY, RENEWAL_EVENT_TYPES, daysLeftLabel } from "@/lib/renewals";
import { typeLabel, fmtDate, fmtMoney } from "@/lib/contracts";
import { cn, dateLocale } from "@/lib/utils";
import api from "@/lib/api";
import { translateFr as tfr } from "@/i18n";

const EVENT_ICONS = {
  note: StickyNote, call: Phone, meeting: Users, offer: HandCoins, counter_offer: Undo2,
  status_change: History, next_action: BellPlus, calendar: CalendarPlus, system: ShieldAlert,
};

const Card = ({ title, children, testid, className }) => (
  <section className={cn("rounded-2xl border border-border bg-surface p-4 sm:p-5", className)} data-testid={testid}>
    {title && <h2 className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-3">{title}</h2>}
    {children}
  </section>
);

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-xs font-medium text-right">{value ?? "—"}</span>
  </div>
);

function PriorityCard({ priority, daysLeft }) {
  const m = RENEWAL_PRIORITY[priority?.level] || RENEWAL_PRIORITY.low;
  return (
    <Card title={tfr("Priorité")} testid="renewal-priority-card">
      <div className="flex items-center gap-3 mb-3">
        <span className={cn("inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold", m.cls)}
          data-testid="renewal-priority-level">{m.label}</span>
        <span className="text-xs text-muted-foreground">{daysLeftLabel(daysLeft)}</span>
      </div>
      <ul className="space-y-1.5" data-testid="renewal-priority-reasons">
        {(priority?.reasons || []).map((r, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-foreground/90">
            <span className={cn("h-1.5 w-1.5 rounded-full mt-1 shrink-0",
              priority.level === "critical" ? "bg-red-400" : priority.level === "high" ? "bg-orange-400" : "bg-blue-400")} />
            {r}
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-muted-foreground mt-3">
        {tfr("Priorité déterministe calculée à partir de vos données réelles (échéance, actions, activité).")}
      </p>
    </Card>
  );
}

function NextActionCard({ data, onSaved }) {
  const [label, setLabel] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const na = data.next_action;

  const plan = async () => {
    if (!label.trim() || !dueAt) return toast.error(tfr("Libellé et échéance requis"));
    setBusy(true);
    try {
      await api.post(`/renewals/${data.id}/next-action`, { label: label.trim(), due_at: new Date(dueAt).toISOString() });
      toast.success(tfr("Prochaine action planifiée"));
      setLabel(""); setDueAt("");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/renewals/${data.id}/next-action/complete`);
      toast.success(tfr("Action terminée"));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Card title={tfr("Prochaine action")} testid="renewal-next-action-card">
      {na ? (
        <div>
          <p className="text-sm font-medium">{na.label}</p>
          <p className="text-xs text-muted-foreground mt-1">Échéance : {fmtDate(na.due_at)} · liée à vos relances</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={complete} disabled={busy}
            data-testid="renewal-complete-action-btn">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marquer comme terminée
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <Input placeholder={tfr("Ex. : Appeler le club pour ouvrir la discussion")} value={label}
            onChange={(e) => setLabel(e.target.value)} data-testid="renewal-action-label-input" />
          <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
            className="min-w-0 w-full" data-testid="renewal-action-due-input" />
          <Button size="sm" onClick={plan} disabled={busy} data-testid="renewal-plan-action-btn">
            <BellPlus className="h-4 w-4 mr-1.5" /> Planifier l'action
          </Button>
          <p className="text-[10px] text-muted-foreground">{tfr("Crée une relance dans votre système « À relancer ».")}</p>
        </div>
      )}
    </Card>
  );
}

function CalendarDialog({ open, onOpenChange, caseId, contractTitle, onSaved }) {
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setTitle(`${tfr("Négociation renouvellement")} — ${contractTitle || ""}`.trim());
  }, [open, contractTitle]);

  const save = async () => {
    if (!title.trim() || !startsAt) return toast.error("Titre et date requis");
    setBusy(true);
    try {
      await api.post(`/renewals/${caseId}/calendar-event`, {
        title: title.trim(), starts_at: new Date(startsAt).toISOString(), location: location.trim(),
      });
      toast.success(tfr("Rendez-vous ajouté au calendrier"));
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="renewal-calendar-dialog">
        <DialogHeader><DialogTitle>{tfr("Planifier un rendez-vous")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="renewal-cal-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Date et heure</Label>
            <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
              className="min-w-0 w-full" data-testid="renewal-cal-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Lieu (optionnel)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="renewal-cal-location-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{tfr("Annuler")}</Button>
          <Button onClick={save} disabled={busy} data-testid="renewal-cal-save-btn">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Planifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryComposer({ caseId, onSaved }) {
  const [type, setType] = useState("note");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!content.trim()) return toast.error(tfr("Le contenu est requis"));
    setBusy(true);
    try {
      await api.post(`/renewals/${caseId}/events`, { type, content: content.trim() });
      setContent("");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2.5" data-testid="renewal-composer">
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="sm:w-[190px] h-9" data-testid="renewal-entry-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RENEWAL_EVENT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea rows={3} placeholder={tfr("Détaillez l'échange, la proposition ou la note…")} value={content}
        onChange={(e) => setContent(e.target.value)} data-testid="renewal-entry-content" />
      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={busy} data-testid="renewal-entry-submit">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Ajouter à l'historique
        </Button>
      </div>
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
}

export default function RenewalWorkspace() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await api.get(`/renewals/${rid}`);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 404) { toast.error(tfr("Dossier introuvable")); navigate("/contracts?tab=renewals"); }
      else setError(true);
    } finally { setLoading(false); }
  }, [rid, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/renewals/${rid}`, { status });
      toast.success(`Statut : ${RENEWAL_STATUS[status]?.label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (error) {
    return (
      <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-16 text-center" data-testid="renewal-workspace-error">
        <p className="text-sm font-medium">{tfr("Impossible de charger ce dossier de renouvellement")}</p>
        <p className="text-xs text-muted-foreground mt-1">{tfr("Vérifiez votre connexion puis réessayez.")}</p>
        <Button size="sm" variant="outline" className="mt-4" onClick={() => { setLoading(true); load(); }}
          data-testid="renewal-workspace-retry-btn">{tfr("Réessayer")}</Button>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const contract = data.contract || {};

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-6 pb-12 space-y-5" data-testid="renewal-workspace">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => navigate("/contracts?tab=renewals")} data-testid="renewal-back-btn"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> {tfr("Renouvellements")}
          </button>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.02em] truncate">
            {data.player_name || data.contract_title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 truncate">
            {data.contract_title} · {typeLabel(data.contract_type)}
            {data.counterparty ? ` · ${data.counterparty}` : ""}
          </p>
        </div>
        <div className="shrink-0">
          <Select value={data.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-[210px]" data-testid="renewal-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RENEWAL_STATUS).map(([id, m]) => <SelectItem key={id} value={id}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          <Card title={tfr("Historique de négociation")} testid="renewal-history-card">
            <HistoryComposer caseId={data.id} onSaved={load} />
            <div className="mt-4 space-y-3" data-testid="renewal-history-list">
              {(data.events || []).length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">{tfr("Aucune entrée pour le moment.")}</p>
              )}
              {(data.events || []).map((ev, i) => {
                const Icon = EVENT_ICONS[ev.type] || StickyNote;
                return (
                  <motion.div key={ev.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(i * 0.03, 0.25) }}
                    className="flex gap-3" data-testid={`renewal-event-${ev.id}`}>
                    <div className="h-8 w-8 rounded-full border border-border bg-secondary/30 flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1 pb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">{ev.type_label}</span>
                        <span className="text-[10px] text-muted-foreground">{fmtDateTime(ev.created_at)} · {ev.actor}</span>
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap break-words">{ev.content}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Card>

          <Card title={tfr("Historique des statuts")} testid="renewal-status-history-card">
            <div className="flex flex-wrap gap-2">
              {[...(data.status_history || [])].reverse().map((h, i) => (
                <div key={i} className="rounded-lg border border-border bg-secondary/30 px-2.5 py-1.5">
                  <p className="text-xs font-medium">{RENEWAL_STATUS[h.status]?.label || h.status}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(h.at)}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <PriorityCard priority={data.priority} daysLeft={data.days_left} />

          <NextActionCard data={data} onSaved={load} />

          <Card title={tfr("Contrat")} testid="renewal-contract-card">
            <InfoRow label="Titre" value={contract.title} />
            <InfoRow label="Type" value={typeLabel(contract.contract_type)} />
            <InfoRow label={tfr("Début")} value={fmtDate(contract.start_date)} />
            <InfoRow label="Fin" value={fmtDate(contract.end_date)} />
            <InfoRow label="Montant" value={fmtMoney(contract.amount_cents, contract.currency)} />
            <InfoRow label="Commission" value={contract.commission_percent != null ? `${contract.commission_percent} %` : "—"} />
            <div className="flex flex-col gap-1.5 mt-3">
              <Link to={`/contracts/${data.contract_id}`} data-testid="renewal-open-contract-link"
                className="inline-flex items-center gap-1.5 text-xs text-violet-400 hover:underline">
                <FileSignature className="h-3.5 w-3.5" /> Ouvrir le contrat
              </Link>
              {data.player_id && (
                <Link to={`/players/${data.player_id}`} data-testid="renewal-open-player-link"
                  className="inline-flex items-center gap-1.5 text-xs text-violet-400 hover:underline">
                  <UserIcon className="h-3.5 w-3.5" /> Voir la fiche athlète
                </Link>
              )}
            </div>
          </Card>

          <Card title={tfr("Calendrier")} testid="renewal-calendar-card">
            {(data.calendar_events || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {data.calendar_events.map((e) => (
                  <div key={e.id} className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
                    <p className="text-xs font-medium truncate">{e.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(e.starts_at)}{e.location ? ` · ${e.location}` : ""}</p>
                  </div>
                ))}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => setCalOpen(true)} data-testid="renewal-open-cal-dialog-btn">
              <CalendarPlus className="h-4 w-4 mr-1.5" /> Planifier un rendez-vous
            </Button>
          </Card>
        </div>
      </div>

      <CalendarDialog open={calOpen} onOpenChange={setCalOpen} caseId={data.id}
        contractTitle={data.contract_title} onSaved={load} />
    </div>
  );
}
